import { uniq } from "lodash";
import { FC } from "react";
import { useHistory, useLocation, useParams } from "react-router";

import { EventPlace, trackEngagement } from "helpers/analytics";
import { useMacros } from "hooks/use-macros";
import { IMacroAction, IMacroApiResponse } from "store/entities/macros/interfaces";

import { AutomationsRoutes } from "../constants";

import MacroEditor, { IMacroForm } from "./components/MacroEditor";
import { IMacroEditorLocationState } from "./interfaces";

interface IProps {
  mode: "add" | "edit";
}

const Macro: FC<IProps> = ({ mode }) => {
  const history = useHistory();
  const location = useLocation<IMacroEditorLocationState>();
  const { macroId } = useParams<{ macroId: string }>();

  const { createMacro, selectMacro, updateMacro } = useMacros();
  const macroFromUrl = selectMacro({ id: macroId });

  // API allows to set teamIds to null, on frontend we should treat it as "all teams". We disable team selection in this case so we need to pass the value unchanged to the API.
  const teamsSetByApi = macroFromUrl && macroFromUrl.teamIds === null;
  const macroTemplate = location.state?.macroTemplate;
  const isInEditMode = mode === "edit";
  const isCreatingFromTemplate = Boolean(macroTemplate);

  const handleSubmit = (values: IMacroForm): void => {
    const analyticsParams = {
      macroType: values.shared ? "shared" : "private",
      numberOfActions: values.actions?.length || 0,
      actionTypesUsed: uniq(values.actions.map(({ type }) => type)),
    };

    if (isInEditMode) {
      updateMacro.mutate(
        {
          id: macroId,
          params: {
            name: values.name,
            description: values.description,
            shared: values.shared,
            teamIds: teamsSetByApi ? macroFromUrl.teamIds : values.teams.map(({ key }) => key),
            actions: values.actions as IMacroAction[],
          },
        },
        {
          onSuccess: () => {
            history.push(`${AutomationsRoutes.Macros}#${macroId}`);
            trackEngagement("Macros - editing: Macro updated", EventPlace.Automations, analyticsParams);
          },
        }
      );
    } else {
      createMacro.mutate(
        {
          params: {
            name: values.name,
            description: values.description,
            shared: values.shared,
            teamIds: teamsSetByApi ? macroFromUrl.teamIds : values.teams.map(({ key }) => key),
            actions: values.actions as IMacroAction[],
          },
        },
        {
          onSuccess: (macro: IMacroApiResponse) => {
            history.push(`${AutomationsRoutes.Macros}#${macro.ID}`);

            if (isCreatingFromTemplate) {
              trackEngagement("Macros: Pre-made macro created", EventPlace.Automations, {
                ...analyticsParams,
                premadeType: macroTemplate.premadeType,
              });
            } else {
              trackEngagement("Macros - editing: Macro created", EventPlace.Automations, analyticsParams);
            }
          },
        }
      );
    }
  };

  return (
    <MacroEditor
      macro={macroFromUrl}
      onSubmit={handleSubmit}
      submitLoading={isInEditMode ? updateMacro.isLoading : createMacro.isLoading}
      submitLabel={isInEditMode ? "Update macro" : "Create macro"}
      headerTitle={isInEditMode ? "Edit macro" : "New macro"}
    />
  );
};

export default Macro;
